import { unavailableTmsOperation } from './tmsConnection';
import { reportClientError } from './clientErrorReporter';
import {
  getPublicPaymentConfigRuntimeConfig,
  loadPublicPaymentConfig,
  loadWebinarPaymentStatus,
  resolvePublicCheckoutPath,
  sanitizePublicPaymentProvider,
} from './publicPaymentConfig';
import {
  attachPublicRequestMetadata,
  buildPublicRequestHeaders,
  resolvePublicRequestId,
} from './publicRequestReliability';
import { buildReachPublicApiUrl, getReachPublicApiBaseUrl } from './reachPublicApi';

const API_BASE_URL = getReachPublicApiBaseUrl();
const DEFAULT_OWNER_UID = String(
  process.env.REACT_APP_CARRIER_FLOW_OWNER_UID
  || process.env.REACT_APP_PUBLIC_INTAKE_OWNER_UID
  || ''
).trim();
const DEFAULT_WORKSPACE_OWNER_UID = String(
  process.env.REACT_APP_CARRIER_FLOW_WORKSPACE_OWNER_UID
  || process.env.REACT_APP_PUBLIC_INTAKE_WORKSPACE_OWNER_UID
  || ''
).trim();
const JOB_APPLICATION_ASSESSMENT_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_JOB_APPLICATION_ASSESSMENT_PATH
  || '/api/public/job-applicants/assessment'
).trim();
const WEBINAR_PAYPAL_CREATE_ORDER_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_PAYPAL_WEBINAR_CREATE_ORDER_PATH
  || '/api/public/webinars/paypal/create-order'
).trim();
const WEBINAR_PAYPAL_CAPTURE_ORDER_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_PAYPAL_WEBINAR_CAPTURE_ORDER_PATH
  || '/api/public/webinars/paypal/capture-order'
).trim();
const WEBINAR_STRIPE_CREATE_CHECKOUT_SESSION_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_STRIPE_WEBINAR_CREATE_CHECKOUT_SESSION_PATH
  || '/api/public/webinars/stripe/create-checkout-session'
).trim();
const WEBINAR_SWIFT_START_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_SWIFT_WEBINAR_START_PATH
  || '/api/public/webinars/swift/start'
).trim();
export const WEBINAR_SINGLE_ACCESS_SKU = 'webinar_single_access';
export const WEBINAR_AGREEMENT_VERSION = 'magellan-prepayment-service-digital-license-2026-06-05';
export const WEBINAR_AGREEMENT_TITLE = 'Magellan Pre-Payment Service Agreement, Digital Product License, Copyright Notice, DMCA Notice, Consulting Disclaimer, and No-Refund Policy';
export const WEBINAR_AGREEMENT_PATH = '/terms-of-use';
export const WEBINAR_AGREEMENT_ACKNOWLEDGMENT = 'I have read and agree to the Magellan Pre-Payment Service Agreement, Digital Product License, Copyright Notice, DMCA Notice, Consulting Disclaimer, and No-Refund Policy. I understand that Magellan provides consulting and educational services only, does not provide legal advice, does not guarantee results, and does not issue refunds after I access, view, download, receive, or use any purchased digital material, webinar link, replay, template, course area, or consulting deliverable, except where required by law.';
const API_REQUEST_TIMEOUT_MS = Number(
  process.env.REACT_APP_CARRIER_FLOW_API_TIMEOUT_MS || process.env.REACT_APP_REQUEST_TIMEOUT_MS || 12000
) || 12000;

const ALLOWED_INTERESTS = new Set(['paperwork', 'tms', 'safety', 'other']);
const PRIVILEGED_PUBLIC_PAYLOAD_KEYS = new Set([
  'ownerUid',
  'workspaceOwnerUid',
  'status',
  'leadId',
  'convertedLeadId',
  'carrierIndexId',
  'conversionDecision',
  'reviewDecision',
  'assessmentDecision',
  'assessmentScore',
  'englishTest',
  'reviewerNotes',
  'applicantStatusMessage',
  'reviewedBy',
  'reviewedByEmail',
  'reviewedAt',
  'consultationPaymentStatus',
  'consultationPaymentAmount',
  'consultationPaymentCurrency',
  'consultationPaymentProvider',
  'consultationPaymentOrderId',
  'consultationPaymentCaptureId',
  'consultationBookingId',
  'bookingId',
  'paymentEventId',
  'paidAt',
  'scheduleStatus',
  'requestedMeetingStart',
  'requestedMeetingEnd',
  'calComBookingUrl',
  'googleCalendarEventId',
  'calendarProvider',
  'calendarLinkId',
  'consultationConfirmedAt',
  'consultationReminder24hState',
  'consultationReminder24hSentAt',
  'consultationReminder1hState',
  'consultationReminder1hSentAt',
]);

function sanitizeText(value) {
  return String(value || '').trim();
}

export function getWebinarAgreementUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${WEBINAR_AGREEMENT_PATH}`;
  }
  return WEBINAR_AGREEMENT_PATH;
}

function buildWebinarAgreementAcceptance(source = {}, options = {}) {
  const accepted = source.termsAccepted === true || source.agreementAccepted === true || options.termsAccepted === true;
  const acceptedAt = sanitizeText(source.agreementAcceptedAt || options.agreementAcceptedAt) || (accepted ? new Date().toISOString() : '');
  return {
    termsAccepted: accepted,
    agreementAccepted: accepted,
    agreementAcceptedAt: accepted ? acceptedAt : '',
    agreementVersion: sanitizeText(source.agreementVersion || options.agreementVersion) || WEBINAR_AGREEMENT_VERSION,
    agreementTitle: sanitizeText(source.agreementTitle || options.agreementTitle) || WEBINAR_AGREEMENT_TITLE,
    agreementUrl: sanitizeText(source.agreementUrl || options.agreementUrl) || getWebinarAgreementUrl(),
    agreementAcknowledgment: WEBINAR_AGREEMENT_ACKNOWLEDGMENT,
  };
}

function buildWebinarSingleAccessProductPayload(webinarId, productSku = WEBINAR_SINGLE_ACCESS_SKU) {
  const normalizedWebinarId = sanitizeText(webinarId);
  return {
    productSku: sanitizeText(productSku) || WEBINAR_SINGLE_ACCESS_SKU,
    productType: 'webinar_single_access',
    selectedProductSku: sanitizeText(productSku) || WEBINAR_SINGLE_ACCESS_SKU,
    selectedWebinarId: normalizedWebinarId,
    webinarId: normalizedWebinarId,
  };
}

function sanitizeSafePublicImageUrl(value) {
  const candidate = sanitizeText(value);
  if (!candidate) {
    return '';
  }
  if (candidate.startsWith('/images/')) {
    return candidate;
  }
  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
}

function sanitizeSafePublicMediaUrl(value) {
  const candidate = sanitizeText(value);
  if (!candidate) {
    return '';
  }
  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
}

function sanitizeYouTubePreviewEmbedUrl(value) {
  const candidate = sanitizeText(value);
  if (!candidate) {
    return '';
  }
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'https:' || url.hostname.toLowerCase() !== 'www.youtube-nocookie.com') {
      return '';
    }
    if (!/^\/embed\/[A-Za-z0-9_-]{11}$/.test(url.pathname)) {
      return '';
    }
    url.searchParams.set('controls', '0');
    url.searchParams.set('disablekb', '1');
    url.searchParams.set('enablejsapi', '1');
    url.searchParams.set('fs', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('rel', '0');
    return url.toString();
  } catch {
    return '';
  }
}

function sanitizePreviewDurationSeconds(value) {
  const duration = Number(value);
  return Number.isFinite(duration) && duration > 0 && duration <= 600
    ? Math.round(duration)
    : null;
}

function sanitizeWebinarPublicTheme(value) {
  const candidate = sanitizeText(value);
  return ['editorial-parallax', 'wine-split', 'data-room', 'process-index'].includes(candidate)
    ? candidate
    : 'editorial-parallax';
}

function sanitizeWebinarVisualTemplate(value) {
  const candidate = sanitizeText(value);
  return ['course-detail', 'editorial-parallax', 'wine-split', 'data-room', 'process-index'].includes(candidate)
    ? candidate
    : 'course-detail';
}

function sanitizeWebinarAccentColor(value, publicTheme = 'editorial-parallax') {
  const candidate = sanitizeText(value);
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return candidate.toLowerCase();
  if (publicTheme === 'wine-split') return '#e9c46a';
  if (publicTheme === 'data-room') return '#34d399';
  if (publicTheme === 'process-index') return '#7dd3fc';
  return '#d4af37';
}

function sanitizePublicNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : null;
}

function sanitizePublicSlug(value) {
  return sanitizeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

function normalizePublicWebinarPreviewFields(webinar = {}) {
  const previewYoutubeEmbedUrl = sanitizeYouTubePreviewEmbedUrl(webinar.previewYoutubeEmbedUrl);
  const previewVideoUrl = sanitizeSafePublicMediaUrl(webinar.previewVideoUrl);
  const previewVideoProvider = sanitizeText(webinar.previewVideoProvider).toLowerCase();
  const previewVideoEnabled = Boolean(webinar.previewVideoEnabled)
    && (
      (previewVideoProvider === 'youtube' && Boolean(previewYoutubeEmbedUrl))
      || (previewVideoProvider === 'upload' && Boolean(previewVideoUrl))
    );

  return {
    previewVideoEnabled,
    previewVideoProvider: previewVideoEnabled ? previewVideoProvider : '',
    previewYoutubeEmbedUrl: previewVideoEnabled ? previewYoutubeEmbedUrl : '',
    previewVideoUrl: previewVideoEnabled ? previewVideoUrl : '',
    previewVideoTitle: previewVideoEnabled ? sanitizeText(webinar.previewVideoTitle).slice(0, 200) : '',
    previewVideoDurationSeconds: previewVideoEnabled ? sanitizePreviewDurationSeconds(webinar.previewVideoDurationSeconds) : null,
    previewCoverImageUrl: previewVideoEnabled ? sanitizeSafePublicImageUrl(webinar.previewCoverImageUrl) : '',
  };
}

function normalizePublicWebinarSpeakerFields(webinar = {}) {
  const sourceSpeaker = webinar.speaker && typeof webinar.speaker === 'object' ? webinar.speaker : {};
  const speakerName = sanitizeText(webinar.speakerName || sourceSpeaker.name).slice(0, 160);
  const speakerTitle = sanitizeText(webinar.speakerTitle || sourceSpeaker.title).slice(0, 180);
  const speakerCompany = sanitizeText(webinar.speakerCompany || sourceSpeaker.company).slice(0, 180);
  const speakerBio = sanitizeText(webinar.speakerBio || sourceSpeaker.bio).slice(0, 2000);
  const speakerHeadshotUrl = sanitizeSafePublicImageUrl(webinar.speakerHeadshotUrl || sourceSpeaker.headshotUrl);
  const speakerProfileSlug = sanitizePublicSlug(webinar.speakerProfileSlug || sourceSpeaker.profileSlug);
  const speakerLocation = sanitizeText(webinar.speakerLocation || sourceSpeaker.location).slice(0, 180);
  const speakerLinkedInUrl = sanitizeSafePublicImageUrl(webinar.speakerLinkedInUrl || sourceSpeaker.linkedInUrl);
  const hasSpeaker = Boolean(
    speakerName
    || speakerTitle
    || speakerCompany
    || speakerBio
    || speakerHeadshotUrl
    || speakerProfileSlug
    || speakerLocation
    || speakerLinkedInUrl
  );

  return {
    speakerName,
    speakerTitle,
    speakerCompany,
    speakerBio,
    speakerHeadshotUrl,
    speakerProfileSlug,
    speakerLocation,
    speakerLinkedInUrl,
    speaker: hasSpeaker
      ? {
          name: speakerName,
          title: speakerTitle,
          company: speakerCompany,
          bio: speakerBio,
          headshotUrl: speakerHeadshotUrl,
          profileSlug: speakerProfileSlug,
          location: speakerLocation,
          linkedInUrl: speakerLinkedInUrl,
        }
      : null,
  };
}

function normalizePublicWebinar(webinar = {}) {
  if (!webinar || typeof webinar !== 'object') {
    return {};
  }
  const publicTheme = sanitizeWebinarPublicTheme(webinar.publicTheme);
  const seats = sanitizePublicNumber(webinar.seats);
  const seatsLeft = sanitizePublicNumber(webinar.seatsLeft);
  return {
    ...webinar,
    publicTheme,
    visualTemplate: sanitizeWebinarVisualTemplate(webinar.visualTemplate),
    accentColor: sanitizeWebinarAccentColor(webinar.accentColor, publicTheme),
    themeLabel: sanitizeText(webinar.themeLabel).slice(0, 140),
    heroImageUrl: sanitizeSafePublicImageUrl(webinar.heroImageUrl),
    enrollmentCloseAt: sanitizeText(webinar.enrollmentCloseAt),
    seats,
    seatsLeft,
    ...normalizePublicWebinarSpeakerFields(webinar),
    ...normalizePublicWebinarPreviewFields(webinar),
  };
}

function normalizePublicSpeakerProfile(speaker = {}) {
  if (!speaker || typeof speaker !== 'object') {
    return {};
  }
  const displayName = sanitizeText(speaker.displayName || speaker.name).slice(0, 160);
  const profileSlug = sanitizePublicSlug(speaker.profileSlug || displayName);
  const headshotUrl = sanitizeSafePublicImageUrl(speaker.headshotUrl);
  const linkedInUrl = sanitizeSafePublicImageUrl(speaker.linkedInUrl);
  const websiteUrl = sanitizeSafePublicImageUrl(speaker.websiteUrl);
  const toLines = (value, maxItems = 24, maxLength = 240) => (
    Array.isArray(value) ? value : String(value || '').split(/\n|,/)
  )
    .map((item) => sanitizeText(item).slice(0, maxLength))
    .filter(Boolean)
    .slice(0, maxItems);

  return {
    id: sanitizeText(speaker.id).slice(0, 240),
    displayName,
    name: displayName,
    profileSlug,
    title: sanitizeText(speaker.title).slice(0, 180),
    company: sanitizeText(speaker.company).slice(0, 180),
    location: sanitizeText(speaker.location).slice(0, 180),
    headline: sanitizeText(speaker.headline).slice(0, 240),
    shortBio: sanitizeText(speaker.shortBio).slice(0, 600),
    fullBio: sanitizeText(speaker.fullBio).slice(0, 6000),
    bio: sanitizeText(speaker.shortBio || speaker.bio || speaker.fullBio).slice(0, 2000),
    previousExperience: toLines(speaker.previousExperience, 20, 240),
    workHistory: toLines(speaker.workHistory, 30, 240),
    credentials: toLines(speaker.credentials, 20, 180),
    specialties: toLines(speaker.specialties, 24, 120),
    trainingFocus: toLines(speaker.trainingFocus, 30, 240),
    languages: toLines(speaker.languages, 12, 80),
    headshotUrl,
    portraitAlt: sanitizeText(speaker.portraitAlt).slice(0, 220),
    linkedInUrl,
    websiteUrl,
    quote: sanitizeText(speaker.quote).slice(0, 600),
    featured: speaker.featured === true,
    sortOrder: Number.isFinite(Number(speaker.sortOrder)) ? Number(speaker.sortOrder) : 0,
    updatedAt: sanitizeText(speaker.updatedAt),
    webinars: [],
  };
}

function unique(values) {
  return Array.from(new Set(values.filter(Boolean)));
}

function getOwnerPayload() {
  const ownerUid = DEFAULT_WORKSPACE_OWNER_UID || DEFAULT_OWNER_UID;
  if (!ownerUid) {
    return {};
  }

  return DEFAULT_WORKSPACE_OWNER_UID
    ? { workspaceOwnerUid: DEFAULT_WORKSPACE_OWNER_UID }
    : { ownerUid: DEFAULT_OWNER_UID };
}

function stripPrivilegedPublicPayloadKeys(payload = {}) {
  return Object.entries(payload).reduce((result, [key, value]) => {
    if (!PRIVILEGED_PUBLIC_PAYLOAD_KEYS.has(key)) {
      result[key] = value;
    }
    return result;
  }, {});
}

function requireApiBaseUrl() {
  return unavailableTmsOperation();
}

function buildApiUrl(path) {
  requireApiBaseUrl();
  return buildReachPublicApiUrl(path);
}

function appendOwnerQuery(rawUrl) {
  const url = new URL(rawUrl, window.location.origin);
  const owner = getOwnerPayload();
  Object.entries(owner).forEach(([key, value]) => {
    const normalizedValue = sanitizeText(value);
    if (normalizedValue) {
      url.searchParams.set(key, normalizedValue);
    }
  });
  return url.pathname.startsWith('/api/reach/public') ? `${url.pathname}${url.search}` : url.toString();
}

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error('The website service returned an invalid response.');
  }
}

async function requestJson() {
  return unavailableTmsOperation();
}

async function requestPublicUrl() {
  return unavailableTmsOperation();
}

async function postJson(path, payload) {
  return requestJson(path, { method: 'POST', payload });
}

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (error) {
    return undefined;
  }
}

function parseFleetSize(value) {
  const digitsOnly = sanitizeText(value).replace(/[^0-9]/g, '');
  if (!digitsOnly) {
    return undefined;
  }

  const parsed = Number(digitsOnly);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function normalizeMoney(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return Number(numeric.toFixed(2));
}

function stripClientOnlyPaymentConfig(payload = {}) {
  const {
    paymentConfig,
    publicPaymentConfig,
    checkoutConfig,
    ...safePayload
  } = payload || {};
  return safePayload;
}

function parseDotMc(value) {
  const raw = sanitizeText(value);
  if (!raw) {
    return { dotNumber: undefined, mcNumber: undefined };
  }

  const dotMatch = raw.match(/(?:^|\b)DOT\s*[:#-]?\s*([0-9]{1,12})/i);
  const mcMatch = raw.match(/(?:^|\b)MC\s*[:#-]?\s*([0-9]{1,12})/i);
  const numericTokens = raw.match(/[0-9]{1,12}/g) || [];

  const dotNumber = dotMatch?.[1] || numericTokens[0] || undefined;
  const mcNumber = mcMatch?.[1] || (numericTokens.length > 1 ? numericTokens[1] : undefined);

  return {
    dotNumber,
    mcNumber,
  };
}

function splitList(value) {
  return unique(
    sanitizeText(value)
      .split(/[\n,;/|]+/)
      .map((entry) => sanitizeText(entry))
  );
}

function inferInterestsFromText(text) {
  const haystack = sanitizeText(text).toLowerCase();
  const interests = [];

  if (!haystack) {
    return interests;
  }

  if (/\btms\b|dispatch|fleet|driver|load|tracking|accounting|billing|maintenance/.test(haystack)) {
    interests.push('tms');
  }
  if (/safety|compliance|dqf|osha|incident|claims|violations?/.test(haystack)) {
    interests.push('safety');
  }
  if (/paperwork|documents?|policy|policies|procedure|training|handbook|forms?/.test(haystack)) {
    interests.push('paperwork');
  }

  return unique(interests.filter((entry) => ALLOWED_INTERESTS.has(entry)));
}

function mapCompanyInterest(value) {
  const normalized = sanitizeText(value).toLowerCase();
  if (!normalized) {
    return { interests: [], otherInterest: undefined };
  }

  if (normalized.includes('tms')) {
    return { interests: ['tms'], otherInterest: undefined };
  }
  if (normalized.includes('safety') || normalized.includes('dqf') || normalized.includes('compliance')) {
    return { interests: ['safety'], otherInterest: undefined };
  }
  if (
    normalized.includes('paperwork')
    || normalized.includes('policy')
    || normalized.includes('procedure')
    || normalized.includes('training')
    || normalized.includes('recruiting')
    || normalized.includes('website')
  ) {
    return { interests: ['paperwork'], otherInterest: undefined };
  }

  return { interests: ['other'], otherInterest: sanitizeText(value) };
}

function buildNotes(sections) {
  return sections
    .map((section) => {
      const label = sanitizeText(section.label);
      const value = sanitizeText(section.value);
      if (!value) {
        return '';
      }
      return `${label}: ${value}`;
    })
    .filter(Boolean)
    .join('\n\n');
}

function makeSuccessResult(data) {
  return {
    success: true,
    data,
  };
}

function makeFailureResult(error) {
  return {
    success: false,
    error: error?.message || 'Request failed.',
    code: error?.code || 'REQUEST_UNAVAILABLE',
    retryable: false,
  };
}

export function extractCarrierFlowReference(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }

  const candidates = [
    data.id,
    data.registrationId,
    data.webinarRegistrationId,
    data.leadId,
    data.publicLeadId,
    data.recordId,
    data.applicantId,
    data.jobApplicantId,
    data.referenceId,
    data.documentId,
    data.requestId,
    data.submissionId,
    data.slug,
    data.record?.id,
  ];

  return candidates.find((value) => sanitizeText(value)) || '';
}

export async function submitWebsiteContact(form) {
  try {
    const name = sanitizeText(form.name);
    const email = sanitizeText(form.email);
    const phone = sanitizeText(form.phone);
    const company = sanitizeText(form.company);
    const subject = sanitizeText(form.subject);
    const message = sanitizeText(form.message);
    const interests = inferInterestsFromText([subject, message, company].filter(Boolean).join(' '));

    const payload = {
      companyName: company || name || 'Website contact inquiry',
      contactName: name,
      email,
      phone,
      interests: interests.length ? interests : ['other'],
      otherInterest: interests.length ? undefined : subject || 'General inquiry',
      notes: buildNotes([
        { label: 'Source page', value: sanitizeText(form.sourcePage) || 'Contact' },
        { label: 'Submitted company', value: company },
        { label: 'Phone', value: phone },
        { label: 'Subject', value: subject },
        { label: 'Message', value: message },
      ]),
      timezone: getBrowserTimezone(),
    };

    const data = await postJson('/api/public/leads', payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_contact_submit_failed', error, { operation: 'submitWebsiteContact' });
    return makeFailureResult(error);
  }
}

export async function submitCompanyApplication(form) {
  try {
    const companyName = sanitizeText(form.companyName);
    const contactName = sanitizeText(form.contactName);
    const email = sanitizeText(form.email);
    const phone = sanitizeText(form.phone);
    const freightType = sanitizeText(form.operationType || form.freightType);
    const painPoints = sanitizeText(form.painPoints);
    const currentSystems = sanitizeText(form.currentSystems);
    const areasNeedAttention = Array.isArray(form.areasNeedAttention)
      ? form.areasNeedAttention.map(sanitizeText).filter(Boolean)
      : splitList(form.areasNeedAttention);
    const fleetSize = parseFleetSize(form.fleetSize);
    const parsedDotMc = parseDotMc(form.dotMc);
    const dotNumber = sanitizeText(form.dotNumber) || parsedDotMc.dotNumber;
    const mcNumber = sanitizeText(form.mcNumber) || parsedDotMc.mcNumber;
    const companyAddress = sanitizeText(form.companyAddress || form.address);
    const companyZipCode = sanitizeText(form.companyZipCode || form.companyZip || form.zipCode || form.zip);
    const companyInterest = mapCompanyInterest(form.interest || areasNeedAttention.join(', '));
    const inferredInterests = inferInterestsFromText([freightType, areasNeedAttention.join(' '), painPoints, currentSystems].filter(Boolean).join(' '));
    const interests = unique([...companyInterest.interests, ...inferredInterests]);

    const payload = {
      companyName,
      contactName,
      email,
      phone,
      address: companyAddress,
      companyAddress,
      companyZipCode,
      dotNumber,
      mcNumber,
      fleetSize,
      interests: interests.length ? interests : ['other'],
      otherInterest: interests.includes('other') ? companyInterest.otherInterest || freightType || painPoints || currentSystems : undefined,
      notes: buildNotes([
        { label: 'Source page', value: 'Company review' },
        { label: 'DBA', value: form.dba },
        { label: 'Submitted DOT / MC', value: form.dotMc },
        { label: 'DOT number', value: dotNumber },
        { label: 'MC number', value: mcNumber },
        { label: 'Company address', value: companyAddress },
        { label: 'Company ZIP code', value: companyZipCode },
        { label: 'Contact title / role', value: form.contactTitle },
        { label: 'Best time to contact', value: form.bestTime },
        { label: 'Preferred contact method', value: form.preferredContactMethod },
        { label: 'Primary operation type', value: freightType },
        { label: 'Areas needing attention', value: areasNeedAttention.join(', ') },
        { label: 'Equipment type', value: form.equipmentType },
        { label: 'Website', value: form.website },
        { label: 'Company location', value: form.location },
        { label: 'Trailer count', value: form.trailerCount },
        { label: 'Driver count', value: form.driverCount },
        { label: 'Driver structure', value: form.driverStructure },
        { label: 'Operating lanes', value: form.operatingLanes },
        { label: 'Years in business', value: form.yearsInBusiness },
        { label: 'Growth stage', value: form.growthStage },
        { label: 'Expected fleet size in 12 months', value: form.expectedFleetSize },
        { label: 'Current TMS', value: form.tms },
        { label: 'Current ELD provider', value: form.eldProvider },
        { label: 'Payroll or settlement system', value: form.payrollSystem },
        { label: 'Driver file storage', value: form.driverFileStorage },
        { label: 'Compliance document management', value: form.complianceManagement },
        { label: 'Training tracking', value: form.trainingTracking },
        { label: 'Recruiting handling', value: form.recruitingHandling },
        { label: 'Safety owner', value: form.safetyOwner },
        { label: 'Paperwork owner', value: form.paperworkOwner },
        { label: 'Driver onboarding owner', value: form.onboardingOwner },
        { label: 'Open problems owner', value: form.openProblemsOwner },
        { label: 'Paperwork condition', value: form.paperworkCondition },
        { label: 'Safety and training status', value: form.safetyTrainingStatus },
        { label: 'Recruiting and onboarding status', value: form.recruitingStatus },
        { label: 'TMS and operations status', value: form.tmsOperationsStatus },
        { label: 'Main problem category', value: form.mainProblem },
        { label: 'Urgency', value: form.urgency },
        { label: 'Pain points', value: painPoints },
        { label: 'Requested service lane', value: form.interest },
        { label: 'What has already been tried', value: currentSystems },
        { label: 'Additional notes', value: form.additionalNotes },
      ]),
      timezone: getBrowserTimezone(),
    };

    const data = await postJson('/api/public/leads', payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_company_submit_failed', error, { operation: 'submitCompanyApplication' });
    return makeFailureResult(error);
  }
}

export async function submitServiceDownloadRequest(form, { service = {}, offer = {} } = {}) {
  try {
    const companyName = sanitizeText(form.companyName);
    const contactName = sanitizeText(form.contactName);
    const email = sanitizeText(form.email);
    const phone = sanitizeText(form.phone);
    const parsedDotMc = parseDotMc(form.dotMc);
    const dotNumber = sanitizeText(form.dotNumber) || parsedDotMc.dotNumber;
    const mcNumber = sanitizeText(form.mcNumber) || parsedDotMc.mcNumber;
    const fleetSize = parseFleetSize(form.fleetSize);
    const serviceSlug = sanitizeText(service.slug);
    const serviceTitle = sanitizeText(service.eyebrow || service.title);
    const downloadOfferId = sanitizeText(offer.offerId);
    const downloadOfferTitle = sanitizeText(offer.title);
    const inferredInterests = inferInterestsFromText([
      serviceTitle,
      service.description,
      offer.title,
      offer.description,
      form.message,
    ].filter(Boolean).join(' '));

    const payload = {
      companyName,
      contactName,
      email,
      phone,
      dotNumber,
      mcNumber,
      fleetSize,
      employeeCount: sanitizeText(form.employeeCount),
      companyZipCode: sanitizeText(form.companyZipCode || form.companyZip),
      companyAddress: sanitizeText(form.companyAddress),
      interests: inferredInterests.length ? inferredInterests : ['paperwork'],
      otherInterest: inferredInterests.length ? undefined : serviceTitle || downloadOfferTitle || 'Service download request',
      sourcePage: serviceSlug ? `/services/${serviceSlug}` : 'service-page',
      downloadSourceType: 'service',
      serviceSlug,
      serviceTitle,
      downloadOfferId,
      downloadOfferTitle,
      notes: buildNotes([
        { label: 'Source page', value: serviceSlug ? `/services/${serviceSlug}` : 'Service download request' },
        { label: 'Service', value: serviceTitle },
        { label: 'Download offer', value: downloadOfferTitle },
        { label: 'Download offer ID', value: downloadOfferId },
        { label: 'Requested file', value: offer.fileName },
        { label: 'Requested file version', value: offer.fileVersion },
        { label: 'Submitted DOT / MC', value: form.dotMc },
        { label: 'DOT number', value: dotNumber },
        { label: 'MC number', value: mcNumber },
        { label: 'Number of trucks', value: form.fleetSize },
        { label: 'Number of employees', value: form.employeeCount },
        { label: 'Company ZIP code', value: form.companyZipCode || form.companyZip },
        { label: 'Operation type', value: form.operationType },
        { label: 'Message', value: form.message },
      ]),
      timezone: getBrowserTimezone(),
    };

    const data = await postJson('/api/public/leads', payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_service_download_submit_failed', error, { operation: 'submitServiceDownloadRequest' });
    return makeFailureResult(error);
  }
}

export async function submitResourceDownloadRequest(form, { resource = {}, offer = {} } = {}) {
  try {
    const companyName = sanitizeText(form.companyName);
    const contactName = sanitizeText(form.contactName);
    const email = sanitizeText(form.email);
    const phone = sanitizeText(form.phone);
    const parsedDotMc = parseDotMc(form.dotMc);
    const dotNumber = sanitizeText(form.dotNumber) || parsedDotMc.dotNumber;
    const mcNumber = sanitizeText(form.mcNumber) || parsedDotMc.mcNumber;
    const fleetSize = parseFleetSize(form.fleetSize);
    const resourceSlug = sanitizeText(resource.slug);
    const resourceTitle = sanitizeText(resource.title);
    const downloadOfferId = sanitizeText(offer.offerId);
    const downloadOfferTitle = sanitizeText(offer.title || resourceTitle);
    const inferredInterests = inferInterestsFromText([
      resourceTitle,
      resource.description,
      offer.title,
      offer.description,
      form.message,
    ].filter(Boolean).join(' '));

    const payload = {
      companyName,
      contactName,
      email,
      phone,
      dotNumber,
      mcNumber,
      fleetSize,
      employeeCount: sanitizeText(form.employeeCount),
      companyZipCode: sanitizeText(form.companyZipCode || form.companyZip),
      companyAddress: sanitizeText(form.companyAddress),
      interests: inferredInterests.length ? inferredInterests : ['paperwork'],
      otherInterest: inferredInterests.length ? undefined : resourceTitle || downloadOfferTitle || 'Resource download request',
      sourcePage: resourceSlug ? `/resources/${resourceSlug}` : 'resource-page',
      downloadSourceType: 'resource',
      resourceSlug,
      resourceTitle,
      downloadOfferId,
      downloadOfferTitle,
      notes: buildNotes([
        { label: 'Source page', value: resourceSlug ? `/resources/${resourceSlug}` : 'Resource download request' },
        { label: 'Resource', value: resourceTitle },
        { label: 'Download offer', value: downloadOfferTitle },
        { label: 'Download offer ID', value: downloadOfferId },
        { label: 'Requested file', value: offer.fileName },
        { label: 'Requested file version', value: offer.fileVersion },
        { label: 'DOT number', value: dotNumber },
        { label: 'MC number', value: mcNumber },
        { label: 'Number of trucks', value: form.fleetSize },
        { label: 'Number of employees', value: form.employeeCount },
        { label: 'Company ZIP code', value: form.companyZipCode || form.companyZip },
        { label: 'Operation type', value: form.operationType },
        { label: 'Message', value: form.message },
      ]),
      timezone: getBrowserTimezone(),
    };

    const data = await postJson('/api/public/leads', payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_resource_download_submit_failed', error, { operation: 'submitResourceDownloadRequest' });
    return makeFailureResult(error);
  }
}

export async function confirmDownloadAccessToken(token) {
  try {
    const normalizedToken = sanitizeText(token);
    if (!normalizedToken) {
      throw new Error('Download confirmation token is missing.');
    }
    const data = await requestJson(`/api/public/download-access/confirm?token=${encodeURIComponent(normalizedToken)}`, {
      method: 'GET',
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_download_confirm_failed', error, { operation: 'confirmDownloadAccessToken' });
    return makeFailureResult(error);
  }
}

export async function requestCustomerAccessLink(email) {
  try {
    const normalizedEmail = sanitizeText(email);
    if (!normalizedEmail) {
      throw new Error('Enter the email used for your Magellan purchase.');
    }
    const data = await postJson('/api/public/customer-access/request', {
      email: normalizedEmail,
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('customer_access_link_request_failed', error, { operation: 'requestCustomerAccessLink' });
    return makeFailureResult(error);
  }
}

export async function verifyCustomerAccessCode(email, code) {
  try {
    const normalizedEmail = sanitizeText(email);
    const normalizedCode = sanitizeText(code);
    if (!normalizedEmail || !normalizedCode) {
      throw new Error('Enter the email and access code from your Magellan email.');
    }
    const data = await postJson('/api/public/customer-access/verify-code', {
      email: normalizedEmail,
      code: normalizedCode,
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('customer_access_code_verify_failed', error, { operation: 'verifyCustomerAccessCode' });
    return makeFailureResult(error);
  }
}

export async function loadCustomerAccessSession(token) {
  try {
    const normalizedToken = sanitizeText(token);
    if (!normalizedToken) {
      throw new Error('Customer access token is missing.');
    }
    const data = await requestJson(`/api/public/customer-access/session?token=${encodeURIComponent(normalizedToken)}`, {
      method: 'GET',
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('customer_access_session_failed', error, { operation: 'loadCustomerAccessSession' });
    return makeFailureResult(error);
  }
}

export async function loadPublicAgreement(token) {
  try {
    const normalizedToken = sanitizeText(token);
    if (!normalizedToken) {
      throw new Error('Agreement token is missing.');
    }
    const data = await requestJson(`/agreements/${encodeURIComponent(normalizedToken)}`, {
      method: 'GET',
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('public_agreement_load_failed', error, { operation: 'loadPublicAgreement' });
    return makeFailureResult(error);
  }
}

export async function signPublicAgreement(token, payload = {}) {
  try {
    const normalizedToken = sanitizeText(token);
    if (!normalizedToken) {
      throw new Error('Agreement token is missing.');
    }
    const signerName = sanitizeText(payload.signerName);
    const signerEmail = sanitizeText(payload.signerEmail);
    if (!signerName || !signerEmail) {
      throw new Error('Signer name and email are required.');
    }
    const data = await postJson(`/agreements/${encodeURIComponent(normalizedToken)}/sign`, {
      signerName,
      signerEmail,
      signerTitle: sanitizeText(payload.signerTitle),
      signatureConsentAccepted: payload.signatureConsentAccepted === true,
      identityVerificationMethod: 'none',
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('public_agreement_sign_failed', error, { operation: 'signPublicAgreement' });
    return makeFailureResult(error);
  }
}

export async function submitCustomerAccessSupport({ token, topic, subject, message, orderId, downloadAccessId, entitlementId, invoiceId, agreementId } = {}) {
  try {
    const normalizedToken = sanitizeText(token);
    const normalizedMessage = sanitizeText(message);
    if (!normalizedToken) {
      throw new Error('Customer access token is missing.');
    }
    if (!normalizedMessage) {
      throw new Error('Enter a support message.');
    }
    const data = await postJson('/api/public/customer-access/support', {
      token: normalizedToken,
      topic: sanitizeText(topic),
      subject: sanitizeText(subject),
      message: normalizedMessage,
      orderId: sanitizeText(orderId),
      downloadAccessId: sanitizeText(downloadAccessId),
      entitlementId: sanitizeText(entitlementId),
      invoiceId: sanitizeText(invoiceId),
      agreementId: sanitizeText(agreementId),
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('customer_access_support_failed', error, { operation: 'submitCustomerAccessSupport' });
    return makeFailureResult(error);
  }
}

export async function requestCustomerDownloadReissue({ token, orderId, downloadAccessId, note } = {}) {
  try {
    const normalizedToken = sanitizeText(token);
    if (!normalizedToken) {
      throw new Error('Customer access token is missing.');
    }
    if (!sanitizeText(orderId) && !sanitizeText(downloadAccessId)) {
      throw new Error('A download or order reference is required.');
    }
    const data = await postJson('/api/public/customer-access/download-reissue', {
      token: normalizedToken,
      orderId: sanitizeText(orderId),
      downloadAccessId: sanitizeText(downloadAccessId),
      note: sanitizeText(note),
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('customer_access_download_reissue_failed', error, { operation: 'requestCustomerDownloadReissue' });
    return makeFailureResult(error);
  }
}

export async function getEmailPreferenceStatus(token, action = '') {
  try {
    const normalizedToken = sanitizeText(token);
    if (!normalizedToken) {
      throw new Error('Email preference token is missing.');
    }
    const query = new URLSearchParams({
      token: normalizedToken,
      format: 'json',
    });
    if (sanitizeText(action)) {
      query.set('action', sanitizeText(action));
    }
    const data = await requestJson(`/api/public/email-preferences?${query.toString()}`, {
      method: 'GET',
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_email_preferences_load_failed', error, { operation: 'getEmailPreferenceStatus' });
    return makeFailureResult(error);
  }
}

export async function updateEmailPreference(token, action) {
  try {
    const normalizedToken = sanitizeText(token);
    const normalizedAction = sanitizeText(action) === 'subscribe' ? 'subscribe' : 'unsubscribe';
    if (!normalizedToken) {
      throw new Error('Email preference token is missing.');
    }
    const data = await requestJson('/api/public/email-preferences?format=json', {
      method: 'POST',
      payload: {
        token: normalizedToken,
        action: normalizedAction,
      },
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_email_preferences_update_failed', error, { operation: 'updateEmailPreference' });
    return makeFailureResult(error);
  }
}

export async function submitJobApplication(form) {
  try {
    const payload = {
      name: sanitizeText(form.name),
      email: sanitizeText(form.email),
      phone: sanitizeText(form.phone),
      desiredRole: sanitizeText(form.role),
      hasIndustryExperience: Boolean(sanitizeText(form.experience)),
      previousExperience: sanitizeText(form.experience)
        ? [{ description: sanitizeText(form.experience) }]
        : [],
      languages: splitList(form.languages),
      availability: buildNotes([
        { label: 'Location', value: form.location },
        { label: 'Availability type', value: form.availability },
        { label: 'Hours per week', value: form.hoursPerWeek },
        { label: 'Preferred working hours', value: form.workingHours },
        { label: 'Start date', value: form.startDate },
        { label: 'Available for U.S. business hours', value: form.usBusinessHours },
      ]),
      equipment: sanitizeText(form.setup),
      notes: buildNotes([
        { label: 'Source page', value: 'Careers application' },
        { label: 'Location', value: form.location },
        { label: 'Time zone', value: form.timeZone },
        { label: 'LinkedIn', value: form.linkedIn },
        { label: 'Portfolio', value: form.portfolio },
        { label: 'Additional role interests', value: form.roleInterests },
        { label: 'Availability type', value: form.availability },
        { label: 'Hours per week', value: form.hoursPerWeek },
        { label: 'Preferred working hours', value: form.workingHours },
        { label: 'Start date', value: form.startDate },
        { label: 'Available for U.S. business hours', value: form.usBusinessHours },
        { label: 'Trucking / logistics experience', value: form.logisticsExperience },
        { label: 'Compliance experience', value: form.complianceExperience },
        { label: 'Sales or cold calling experience', value: form.salesExperience },
        { label: 'Writing / SOP experience', value: form.writingExperience },
        { label: 'Administrative / customer support experience', value: form.adminExperience },
        { label: 'Tools', value: form.tools },
        { label: 'Written English level', value: form.writtenEnglish },
        { label: 'Spoken English level', value: form.spokenEnglish },
        { label: 'Comfort with business calls', value: form.callComfort },
        { label: 'Comfort with cold calls', value: form.coldCallComfort },
        { label: 'Comfort writing SOPs or instructions', value: form.sopComfort },
        { label: 'Work sample', value: form.workSample },
        { label: 'Why fit', value: form.whyFit },
        { label: 'Best work type', value: form.bestAt },
        { label: 'Messy information example', value: form.messyInfo },
        { label: 'Magellan explanation sample', value: form.explainMagellan },
        { label: 'Reliability statement', value: form.reliability },
        { label: 'Work applicant does not want', value: form.avoidWork },
        { label: 'Handling unclear instructions', value: form.unclearInstructions },
      ]),
    };

    const data = await postJson('/api/public/job-applicants', payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_job_submit_failed', error, { operation: 'submitJobApplication' });
    return makeFailureResult(error);
  }
}

export async function submitJobApplicationAssessment(payload) {
  try {
    const data = await postJson(JOB_APPLICATION_ASSESSMENT_PATH, payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_job_assessment_submit_failed', error, { operation: 'submitJobApplicationAssessment' });
    return makeFailureResult(error);
  }
}

export async function submitWebinarRegistration(form, webinarId, options = {}) {
  try {
    const normalizedWebinarId = sanitizeText(webinarId);
    const attendeeType = sanitizeText(form.attendeeType) || 'other';
    const attendeeTypeLabel = attendeeType === 'company'
      ? 'Carrier / Fleet Company'
      : attendeeType === 'employee'
        ? 'Employee / Team Member'
      : attendeeType === 'broker'
        ? 'Freight Broker'
        : 'Other / Independent';
    const dotNumber = sanitizeText(form.dotNumber);
    const mcNumber = sanitizeText(form.mcNumber);
    const truckCount = sanitizeText(form.truckCount);
    const employeeCount = sanitizeText(form.employeeCount);
    const companyZipCode = sanitizeText(form.companyZipCode);
    const companyAddress = sanitizeText(form.companyAddress);
    const roleOrTitle = sanitizeText(form.roleOrTitle);

    if (!normalizedWebinarId || normalizedWebinarId === 'general') {
      const data = await postJson('/api/public/leads', {
        companyName: sanitizeText(form.company) || sanitizeText(form.name) || 'Course Interest',
        contactName: sanitizeText(form.name),
        contactTitle: 'Course interest',
        email: sanitizeText(form.email),
        phone: sanitizeText(form.phone),
        interests: ['other'],
        otherInterest: 'Course interest',
        notes: buildNotes([
          { label: 'Source page', value: 'Courses interest form' },
          { label: 'Submission type', value: 'General course interest' },
          { label: 'Attendee type', value: attendeeTypeLabel },
          { label: 'Role / title', value: roleOrTitle },
          { label: 'DOT number', value: dotNumber },
          { label: 'MC number', value: mcNumber },
          { label: 'Number of trucks', value: truckCount },
          { label: 'Number of employees', value: employeeCount },
          { label: 'Company ZIP code', value: companyZipCode },
          { label: 'Company address', value: companyAddress },
          { label: 'Payment status', value: options.paymentStatus },
        ]),
        dotNumber,
        mcNumber,
        fleetSize: parseFleetSize(truckCount),
        employeeCount,
        companyZipCode,
        companyAddress,
        contactTitle: roleOrTitle || 'Webinar interest',
        webinarPaymentStatus: sanitizeText(options.paymentStatus) || 'not_required',
        webinarPaymentRequired: Boolean(options.paymentRequired),
        webinarPaymentAmount: normalizeMoney(options.paymentAmount),
        webinarPaymentCurrency: sanitizeText(options.paymentCurrency) || 'USD',
        webinarPaymentProvider: sanitizePublicPaymentProvider(options.paymentProvider, 'manual'),
        timezone: getBrowserTimezone(),
      });
      return makeSuccessResult(data);
    }

    const paymentRequired = Boolean(options.paymentRequired);
    const agreementAcceptance = paymentRequired ? buildWebinarAgreementAcceptance(form, options) : {};
    const payload = {
      ...buildWebinarSingleAccessProductPayload(normalizedWebinarId, options.productSku),
      webinarId: normalizedWebinarId,
      name: sanitizeText(form.name),
      email: sanitizeText(form.email),
      phone: sanitizeText(form.phone),
      company: sanitizeText(form.company),
      attendeeType,
      attendeeTypeLabel,
      roleOrTitle,
      dotNumber,
      mcNumber,
      truckCount,
      fleetSize: parseFleetSize(truckCount),
      employeeCount,
      companyZipCode,
      companyAddress,
      webinarTitle: sanitizeText(options.webinarTitle),
      paymentRequired,
      paymentStatus: paymentRequired ? 'pending' : 'not_required',
      paymentProvider: paymentRequired ? sanitizePublicPaymentProvider(options.paymentProvider, 'manual') : 'manual',
      paymentAmount: normalizeMoney(options.paymentAmount),
      paymentCurrency: sanitizeText(options.paymentCurrency) || 'USD',
      timezone: getBrowserTimezone(),
      ...agreementAcceptance,
    };

    const data = await postJson('/api/public/webinars/register', payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_webinar_submit_failed', error, { operation: 'submitWebinarRegistration' });
    return makeFailureResult(error);
  }
}

export async function loadWebinarPaymentConfig(webinarId, options = {}) {
  return loadPublicPaymentConfig({
    ...options,
    flow: 'webinar',
    webinarId,
  });
}

export async function confirmWebinarPaymentStatus(options = {}) {
  return loadWebinarPaymentStatus(options);
}

function buildWebinarPaymentPayload({ form = {}, webinar = {}, registrationReference = '' } = {}) {
  const webinarId = sanitizeText(webinar.id || webinar.slug);
  const agreementAcceptance = buildWebinarAgreementAcceptance(form);
  return {
    ...buildWebinarSingleAccessProductPayload(webinarId, webinar.productSku),
    webinarId,
    webinarTitle: sanitizeText(webinar.title),
    amount: normalizeMoney(webinar.paymentAmount ?? webinar.priceAmount ?? webinar.amount ?? webinar.price),
    currency: sanitizeText(webinar.currency || webinar.paymentCurrency) || 'USD',
    registrationReference: sanitizeText(registrationReference),
    name: sanitizeText(form.name),
    email: sanitizeText(form.email),
    phone: sanitizeText(form.phone),
    company: sanitizeText(form.company),
    attendeeType: sanitizeText(form.attendeeType) || 'other',
    roleOrTitle: sanitizeText(form.roleOrTitle),
    dotNumber: sanitizeText(form.dotNumber),
    mcNumber: sanitizeText(form.mcNumber),
    truckCount: sanitizeText(form.truckCount),
    employeeCount: sanitizeText(form.employeeCount),
    companyZipCode: sanitizeText(form.companyZipCode),
    companyAddress: sanitizeText(form.companyAddress),
    returnUrl: sanitizeText(webinar.returnUrl),
    cancelUrl: sanitizeText(webinar.cancelUrl),
    timezone: getBrowserTimezone(),
    ...agreementAcceptance,
  };
}

export async function createWebinarPayPalOrder({ form = {}, webinar = {}, registrationReference = '', paymentConfig } = {}) {
  try {
    const payload = buildWebinarPaymentPayload({ form, webinar, registrationReference });
    const checkoutPath = resolvePublicCheckoutPath(
      paymentConfig || webinar.paymentConfig || webinar.publicPaymentConfig,
      'paypal',
      'createOrderPath',
      WEBINAR_PAYPAL_CREATE_ORDER_PATH
    );
    const data = await postJson(checkoutPath, payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_webinar_paypal_create_failed', error, { operation: 'createWebinarPayPalOrder' });
    return makeFailureResult(error);
  }
}

export async function captureWebinarPayPalOrder(payload = {}, options = {}) {
  try {
    const paymentConfig = options.paymentConfig || payload.paymentConfig || payload.publicPaymentConfig;
    const checkoutPath = resolvePublicCheckoutPath(
      paymentConfig,
      'paypal',
      'captureOrderPath',
      WEBINAR_PAYPAL_CAPTURE_ORDER_PATH
    );
    const data = await postJson(checkoutPath, {
      ...stripClientOnlyPaymentConfig(payload),
      orderId: sanitizeText(payload.orderId),
      token: sanitizeText(payload.token),
      payerId: sanitizeText(payload.payerId),
      webinarId: sanitizeText(payload.webinarId),
      productSku: sanitizeText(payload.productSku) || WEBINAR_SINGLE_ACCESS_SKU,
      productType: sanitizeText(payload.productType) || 'webinar_single_access',
      selectedProductSku: sanitizeText(payload.selectedProductSku || payload.productSku) || WEBINAR_SINGLE_ACCESS_SKU,
      selectedWebinarId: sanitizeText(payload.selectedWebinarId || payload.webinarId),
      webinarTitle: sanitizeText(payload.webinarTitle),
      registrationReference: sanitizeText(payload.registrationReference),
      name: sanitizeText(payload.name),
      email: sanitizeText(payload.email),
      phone: sanitizeText(payload.phone),
      company: sanitizeText(payload.company),
      attendeeType: sanitizeText(payload.attendeeType) || 'other',
      roleOrTitle: sanitizeText(payload.roleOrTitle),
      dotNumber: sanitizeText(payload.dotNumber),
      mcNumber: sanitizeText(payload.mcNumber),
      truckCount: sanitizeText(payload.truckCount),
      employeeCount: sanitizeText(payload.employeeCount),
      companyZipCode: sanitizeText(payload.companyZipCode),
      companyAddress: sanitizeText(payload.companyAddress),
      amount: normalizeMoney(payload.amount),
      currency: sanitizeText(payload.currency) || 'USD',
      timezone: getBrowserTimezone(),
      ...buildWebinarAgreementAcceptance(payload),
    });
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_webinar_paypal_capture_failed', error, { operation: 'captureWebinarPayPalOrder' });
    return makeFailureResult(error);
  }
}

export async function createWebinarStripeCheckoutSession({ form = {}, webinar = {}, registrationReference = '', paymentConfig } = {}) {
  try {
    const payload = buildWebinarPaymentPayload({ form, webinar, registrationReference });
    const checkoutPath = resolvePublicCheckoutPath(
      paymentConfig || webinar.paymentConfig || webinar.publicPaymentConfig,
      'stripe',
      'createCheckoutSessionPath',
      WEBINAR_STRIPE_CREATE_CHECKOUT_SESSION_PATH
    );
    const data = await postJson(checkoutPath, payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_webinar_stripe_create_failed', error, { operation: 'createWebinarStripeCheckoutSession' });
    return makeFailureResult(error);
  }
}

export async function startWebinarSwiftPayment({ form = {}, webinar = {}, registrationReference = '', paymentConfig } = {}) {
  try {
    const payload = buildWebinarPaymentPayload({ form, webinar, registrationReference });
    const checkoutPath = resolvePublicCheckoutPath(
      paymentConfig || webinar.paymentConfig || webinar.publicPaymentConfig,
      'swift',
      'startPath',
      WEBINAR_SWIFT_START_PATH
    );
    const data = await postJson(checkoutPath, payload);
    return makeSuccessResult(data);
  } catch (error) {
    reportClientError('carrier_flow_webinar_swift_start_failed', error, { operation: 'startWebinarSwiftPayment' });
    return makeFailureResult(error);
  }
}

export async function fetchPublicWebinars() {
  try {
    const baseUrl = requireApiBaseUrl();
    const data = await requestPublicUrl(appendOwnerQuery(`${baseUrl}/api/public/webinars`));
    return makeSuccessResult(Array.isArray(data.webinars) ? data.webinars.map(normalizePublicWebinar) : []);
  } catch (error) {
    reportClientError('carrier_flow_webinar_feed_failed', error, { operation: 'fetchPublicWebinars' });
    return makeFailureResult(error);
  }
}

export async function fetchPublicSpeakers() {
  try {
    const baseUrl = requireApiBaseUrl();
    const data = await requestPublicUrl(appendOwnerQuery(`${baseUrl}/api/public/speakers`));
    return makeSuccessResult(Array.isArray(data.speakers) ? data.speakers.map(normalizePublicSpeakerProfile).filter((speaker) => speaker.name || speaker.headshotUrl) : []);
  } catch (error) {
    reportClientError('carrier_flow_speaker_feed_failed', error, { operation: 'fetchPublicSpeakers' });
    return makeFailureResult(error);
  }
}

export async function fetchPublicSpeakerProfile(slug) {
  try {
    const safeSlug = sanitizePublicSlug(slug);
    if (!safeSlug) {
      throw new Error('Speaker profile slug is required.');
    }
    const baseUrl = requireApiBaseUrl();
    const data = await requestPublicUrl(appendOwnerQuery(`${baseUrl}/api/public/speakers/${encodeURIComponent(safeSlug)}`));
    return makeSuccessResult(data.speaker ? normalizePublicSpeakerProfile(data.speaker) : null);
  } catch (error) {
    reportClientError('carrier_flow_speaker_profile_failed', error, { operation: 'fetchPublicSpeakerProfile' });
    return makeFailureResult(error);
  }
}

export function getCarrierFlowRuntimeConfig() {
  return {
    apiBaseUrl: API_BASE_URL,
    ownerUid: DEFAULT_OWNER_UID,
    workspaceOwnerUid: DEFAULT_WORKSPACE_OWNER_UID,
    jobApplicationAssessmentPath: JOB_APPLICATION_ASSESSMENT_PATH,
    webinarPayPalCreateOrderPath: WEBINAR_PAYPAL_CREATE_ORDER_PATH,
    webinarPayPalCaptureOrderPath: WEBINAR_PAYPAL_CAPTURE_ORDER_PATH,
    webinarStripeCreateCheckoutSessionPath: WEBINAR_STRIPE_CREATE_CHECKOUT_SESSION_PATH,
    webinarSwiftStartPath: WEBINAR_SWIFT_START_PATH,
    publicPayment: getPublicPaymentConfigRuntimeConfig(),
  };
}
